import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Chip,
  useMediaQuery,
  CircularProgress as MUICircularProgress,
} from "@material-ui/core";

import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  GetApp as GetAppIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import WebhookModal from "../../components/WebhookModal";

import {
  Add as AddIcon,
  Upload as UploadIcon,
  DevicesFold as DevicesFoldIcon,
  AddCircle,
  Build,
  ContentCopy,
  WebhookOutlined,
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
} from "@mui/material";

import FlowBuilderModal from "../../components/FlowBuilderModal";

import {
  colorBackgroundTable,
  colorLineTable,
  colorLineTableHover,
  colorPrimary,
  colorTitleTable,
  colorTopTable,
} from "../../styles/styles";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    ...theme.scrollbarStyles,
    border: "none",
  },
  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  searchPaper: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.7)",
      opacity: 1,
    },
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light" ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },
  tabContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  premiumCard: {
    borderRadius: "15px",
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    border: `1px solid ${theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
    "&:hover": {
      boxShadow: "0 8px 30px rgba(101, 23, 171, 0.15)",
      transform: "translateY(-5px)",
    },
  },
  iconButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  tableRow: {
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(101, 23, 171, 0.05)",
    },
    cursor: "pointer",
  },
  statusChip: {
    fontWeight: 700,
    borderRadius: "8px",
  },
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [navigationConfirmOpen, setNavigationConfirmOpen] = useState(false);
  const [successfulImport, setSuccessfulImport] = useState(false);
  const [tabValue, setTabValue] = useState("table");

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };

    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = () => {
    setSelectedContactId(deletingContact.id);
    setSelectedWebhookName(deletingContact.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`).then((res) => {
        setDeletingContact(null);
        setReloadData((old) => !old);
      });
      toast.success("Fluxo excluído com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api
        .post(`/flowbuilder/duplicate`, { flowId: flowId })
        .then((res) => {
          setDeletingContact(null);
          setReloadData((old) => !old);
        });
      toast.success("Fluxo duplicado com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderCardActions = (contact) => (
    <Box display="flex" gap={1} justifyContent="center" width="100%" mt={tabValue === "grid" ? 2 : 0}>
      <IconButton
        size="small"
        onClick={() => {
          setSelectedContactId(contact.id);
          setSelectedWebhookName(contact.name);
          setContactModalOpen(true);
        }}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", color: "#1976d2" }}
        className={classes.iconButton}
        title="Editar Nome"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => history.push(`/flowbuilder/${contact.id}`)}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(101, 23, 171, 0.1)" : "#f3e5f5", color: "#6517ab" }}
        className={classes.iconButton}
        title="Editar Fluxo"
      >
        <Build fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleExportFlow(contact.id)}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(46, 125, 50, 0.1)" : "#e8f5e9", color: "#2e7d32" }}
        className={classes.iconButton}
        title="Exportar Fluxo"
      >
        <GetAppIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setDeletingContact(contact);
          setConfirmDuplicateOpen(true);
        }}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(245, 158, 11, 0.1)" : "#fff8e1", color: "#f59e0b" }}
        className={classes.iconButton}
        title="Duplicar Fluxo"
      >
        <ContentCopy fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setDeletingContact(contact);
          setConfirmOpen(true);
        }}
        style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", color: "#d32f2f" }}
        className={classes.iconButton}
        title="Excluir Fluxo"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const exportLink = () => {
    history.push(`/flowbuilder/${deletingContact.id}`);
  };

  const handleExportFlow = async (flowId) => {
    try {
      toast.info("Preparando exportação do fluxo...");

      const response = await api.get(`/flowbuilder/export/${flowId}`, {
        responseType: "blob",
      });

      if (response.data.size === 0) {
        toast.error("Erro: O arquivo exportado está vazio");
        return;
      }

      const flowToExport = webhooks.find((wh) => wh.id === flowId);
      const flowName = flowToExport ? flowToExport.name : "fluxo";

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${flowName.replace(/\s+/g, "_").toLowerCase()}_export.json`
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      toast.success("Fluxo exportado com sucesso");
    } catch (error) {
      console.error("Erro ao exportar fluxo:", error);
      toast.error(
        "Erro ao exportar fluxo: " +
        (error.response?.data?.error || "Erro desconhecido")
      );
    }
  };

  const handleImportFlow = async () => {
    if (!importFile) {
      toast.error("Selecione um arquivo para importar");
      return;
    }

    if (!importFile.name.toLowerCase().endsWith(".json")) {
      toast.error("O arquivo deve ser do tipo JSON");
      return;
    }

    try {
      setImportLoading(true);
      toast.info("Importando fluxo, por favor aguarde...");

      const formData = new FormData();
      formData.append("file", importFile);

      const response = await api.post("/flowbuilder/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(`Fluxo "${response.data.name}" importado com sucesso!`);
      setImportModalOpen(false);
      setImportFile(null);
      setReloadData((old) => !old);

      setSuccessfulImport(true);
      setNavigationConfirmOpen(true);
    } catch (error) {
      console.error("Erro ao importar fluxo:", error);
      const errorMsg = error.response?.data?.error || "Erro desconhecido";
      toast.error(`Erro ao importar fluxo: ${errorMsg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleNavigationConfirm = () => {
    setReloadData((old) => !old);
    setNavigationConfirmOpen(false);
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      ></FlowBuilderModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name
            }?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={(e) =>
          deletingContact ? handleDeleteWebhook(deletingContact.id) : () => { }
        }
      >
        {deletingContact
          ? `Tem certeza que deseja deletar este fluxo? Todas as integrações relacionados serão perdidos.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `Deseja duplicar o fluxo ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={(e) =>
          deletingContact ? handleDuplicateFlow(deletingContact.id) : () => { }
        }
      >
        {deletingContact
          ? `Tem certeza que deseja duplicar este fluxo?`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <Dialog open={importModalOpen} onClose={() => setImportModalOpen(false)}>
        <DialogTitle>Importar Fluxo</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Selecione um arquivo JSON exportado do FlowBuilder para importar.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                border: "1px dashed #ccc",
                borderRadius: "4px",
                padding: "16px",
                marginTop: "16px",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "#0872B9",
                },
              }}
            >
              <Input
                type="file"
                id="flow-import"
                accept="application/json"
                style={{ display: "none" }}
                onChange={(e) => setImportFile(e.target.files[0])}
              />
              <label htmlFor="flow-import">
                <UploadIcon sx={{ color: "#0872B9", fontSize: "40px" }} />
                <Typography
                  variant="body1"
                  sx={{ color: "#0872B9", marginTop: "8px" }}
                >
                  {importFile
                    ? `${importFile.name} (${(importFile.size / 1024).toFixed(
                      2
                    )} KB)`
                    : "Escolha um arquivo JSON"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", marginTop: "4px" }}
                >
                  {importFile
                    ? "Arquivo selecionado"
                    : "Clique para selecionar o arquivo"}
                </Typography>
              </label>
            </Box>
            {importFile && (
              <Typography
                variant="body2"
                sx={{ color: "green", mt: 2, textAlign: "center" }}
              >
                Arquivo pronto para importação! Clique em "Importar" para
                continuar.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleImportFlow}
            disabled={!importFile || importLoading}
            variant="contained"
            color="primary"
          >
            {importLoading ? <CircularProgress size={24} /> : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={navigationConfirmOpen}
        onClose={() => setNavigationConfirmOpen(false)}
      >
        <DialogTitle>Importação Concluída</DialogTitle>
        <DialogContent>
          <Typography>
            {successfulImport
              ? "Fluxo importado com sucesso! A lista foi atualizada."
              : "A lista de fluxos foi atualizada."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNavigationConfirmOpen(false)}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Box className={classes.headerContainer}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" className={classes.headerTitle}>Flow Builder</Typography>
            <Typography className={classes.headerSubtitle}>Crie e gerencie fluxos de automação inteligentes</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent={isMobile ? "center" : "flex-end"}>
              <Paper className={classes.searchPaper} elevation={0}>
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                </InputAdornment>
                <TextField
                  placeholder="Pesquisar..."
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{ disableUnderline: true }}
                />
              </Paper>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<UploadIcon />}
                onClick={() => setImportModalOpen(true)}
              >
                Importar
              </Button>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddIcon />}
                onClick={handleOpenContactModal}
              >
                Novo Fluxo
              </Button>
            </Box>
          </Grid>
        </Grid>

        <div className={classes.tabContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className={classes.tabs}
          >
            <Tab label="Lista" value="table" className={classes.tab} />
            <Tab label="Grade" value="grid" className={classes.tab} />
          </Tabs>
        </div>
      </Box>

      <Paper
        className={classes.mainPaper}
        elevation={0}
        onScroll={handleScroll}
      >
        {tabValue === "table" && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome do Fluxo</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map((contact) => (
                <TableRow key={contact.id} className={classes.tableRow}>
                  <TableCell onClick={() => history.push(`/flowbuilder/${contact.id}`)}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar style={{ backgroundColor: "#6517ab", width: 40, height: 40 }}>
                        <DevicesFoldIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {contact.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {contact.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={contact.active ? "Ativo" : "Desativado"}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: contact.active ? "rgba(16, 185, 129, 0.1)" : "rgba(107, 114, 128, 0.1)",
                        color: contact.active ? "#10b981" : "#6b7280",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {renderCardActions(contact)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={3} />}
            </TableBody>
          </Table>
        )}

        {tabValue === "grid" && (
          <Grid container spacing={3} style={{ padding: "16px" }}>
            {webhooks.map((contact) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={contact.id}>
                <Card className={classes.premiumCard}>
                  <CardContent style={{ textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Avatar style={{ backgroundColor: "#6517ab", width: 60, height: 60 }}>
                        <DevicesFoldIcon style={{ fontSize: 30 }} />
                      </Avatar>
                    </Box>
                    <Typography variant="h6" style={{ fontWeight: 700, color: theme.palette.text.primary }} gutterBottom>
                      {contact.name}
                    </Typography>
                    <Box display="flex" justifyContent="center" mt={1} mb={2}>
                      <Chip
                        label={contact.active ? "Ativo" : "Desativado"}
                        size="small"
                        className={classes.statusChip}
                        style={{
                          backgroundColor: contact.active ? "rgba(16, 185, 129, 0.1)" : "rgba(107, 114, 128, 0.1)",
                          color: contact.active ? "#10b981" : "#6b7280",
                        }}
                      />
                    </Box>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="caption" color="textSecondary" display="block">
                      ID: <b>{contact.id}</b>
                    </Typography>
                    {renderCardActions(contact)}
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {loading && (
              <Grid item xs={12} align="center">
                <CircularProgress color="primary" />
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    </MainContainer>
  );
};

export default FlowBuilder;
